import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaInstagram, FaTiktok, FaPinterest } from "react-icons/fa";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/cc611435-5176-48e2-bb46-da64f5f3bc20/images/ddd676f739471001389033821ded28a8b177a1d2401696f3d638cc7881c63b1c.png";
const BG_SILK = "https://static.prod-images.emergentagent.com/jobs/cc611435-5176-48e2-bb46-da64f5f3bc20/images/4ca48871e651ecdd3574ff0c14abea037a2efdeba5ce68f227980758fa011d4a.png";

const PLATFORM_ICON = {
  instagram: FaInstagram, tiktok: FaTiktok, pinterest: FaPinterest,
};

const aspectClass = (a) => a === "portrait" ? "aspect-[3/4]" : a === "landscape" ? "aspect-[4/3]" : "aspect-square";

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/social-posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const s = settings || {};
  const categories = (s.categories && s.categories.length) ? s.categories : [];
  const shownProducts = activeCategory
    ? products.filter((p) => p.category === activeCategory)
    : products.filter((p) => p.featured);
  const socials = [
    { href: s.instagram_url, Icon: FaInstagram, testid: "social-instagram", label: "Instagram" },
    { href: s.tiktok_url, Icon: FaTiktok, testid: "social-tiktok", label: "TikTok" },
    { href: s.pinterest_url, Icon: FaPinterest, testid: "social-pinterest", label: "Pinterest" },
  ].filter((x) => x.href);

  const selectCategory = (name) => {
    setActiveCategory(name);
    setTimeout(() => {
      document.getElementById("products")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <div className="grain min-h-screen bg-cream text-ink font-sans">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 bg-cream/80 backdrop-blur-xl border-b border-ink/10" data-testid="site-header">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <a href="#top" className="font-serif text-xl md:text-2xl tracking-tight" data-testid="brand-logo">
            Charlie&rsquo;s <span className="italic text-magenta">Digital</span> Collective
          </a>
          <nav className="hidden md:flex items-center gap-8 text-xs font-bold uppercase tracking-[0.2em]">
            <a href="#gallery" className="hover:text-magenta transition-colors" data-testid="nav-gallery">Feed</a>
            <a href="#categories" className="hover:text-magenta transition-colors" data-testid="nav-categories">Categories</a>
            <a href="#products" className="hover:text-magenta transition-colors" data-testid="nav-products">Edit</a>
            <a href="#partner" className="hover:text-magenta transition-colors" data-testid="nav-partner">Partner</a>
          </nav>
          <a href="#partner" className="hidden md:inline-flex items-center gap-2 px-5 py-3 bg-ink text-cream text-xs font-bold uppercase tracking-widest hover:bg-magenta transition-colors" data-testid="header-cta">
            Work with me <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="pt-32 md:pt-40 pb-20 md:pb-28" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-5xl">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-magenta mb-6">
              <Sparkles className="w-4 h-4" /> Affiliate & Digital Marketing · Est. 2025
            </div>
            <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight font-medium">
              {(() => {
                const accentSet = new Set(
                  (s.hero_accent_words || "").split(",").map((w) => w.trim().toLowerCase()).filter(Boolean)
                );
                const words = (s.hero_tagline || "A curated collective of pieces worth buying.").split(" ");
                return words.map((w, i) => {
                  const key = w.toLowerCase().replace(/[^a-z0-9]/g, "");
                  const isAccent = accentSet.has(key);
                  return (
                    <span key={i} className={isAccent ? "text-magenta italic" : ""}>
                      {w}{" "}
                    </span>
                  );
                });
              })()}
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/70">
              {s.hero_subtitle || "By Charlie Smith. A working archive of premium brands and digital products."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#products" className="inline-flex items-center gap-2 px-8 py-4 bg-magenta text-cream text-xs font-bold uppercase tracking-widest hover:bg-ink transition-colors" data-testid="hero-shop-cta">
                Browse the edit <ArrowUpRight className="w-4 h-4" />
              </a>
              <a href="#partner" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-ink text-ink text-xs font-bold uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors" data-testid="hero-partner-cta">
                Affiliate partners
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-ink overflow-hidden py-5 bg-cream" data-testid="marquee">
        <div className="marquee flex gap-16 whitespace-nowrap font-serif text-2xl md:text-3xl italic">
          {[...Array(2)].map((_, idx) => {
            const items = (s.marquee_items && s.marquee_items.length)
              ? s.marquee_items
              : ["Luxury Fashion", "Home & Design", "Private Travel", "Beauty Rituals", "Digital Products", "Considered Living"];
            return (
              <div key={idx} className="flex gap-16 shrink-0 items-center">
                {items.map((label, i) => (
                  <React.Fragment key={i}>
                    <span>{label}</span>
                    <span className="text-magenta">&diams;</span>
                  </React.Fragment>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Social Gallery */}
      <section id="gallery" className="py-24 md:py-32" data-testid="gallery-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-magenta mb-3">The Feed</p>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1]">Straight from <span className="italic">my socials</span>.</h2>
            </div>
            <div className="flex gap-4">
              {socials.map(({ href, Icon, testid }) => (
                <a key={testid} href={href} target="_blank" rel="noreferrer" className="p-3 border border-ink hover:bg-magenta hover:border-magenta hover:text-cream transition-colors" data-testid={testid}><Icon className="w-5 h-5" /></a>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {posts.map((p, i) => {
              const Icon = PLATFORM_ICON[p.platform] || FaInstagram;
              const tall = i % 5 === 0 || i % 5 === 3;
              return (
                <a key={p.id} href={p.post_url} target="_blank" rel="noreferrer"
                   className={`group relative overflow-hidden bg-ink ${tall ? "row-span-2 aspect-[3/4]" : aspectClass(p.aspect)}`}
                   data-testid={`social-feed-item-${i}`}>
                  <img src={p.image_url} alt={p.caption} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-3 left-3 flex items-center gap-2 text-cream">
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-ink/60 px-2 py-1">{p.post_type}</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-cream translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                    <p className="text-sm">{p.caption}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="py-24 md:py-32 bg-cream-2" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-magenta mb-3">Categories</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1]">Five worlds. <span className="italic">One taste.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
            {categories.map((c, i) => (
              <button key={c.name} type="button" onClick={() => selectCategory(c.name)}
                 className={`group relative overflow-hidden bg-ink text-left ${i === 0 ? "md:col-span-7 aspect-[16/10]" : i === 1 ? "md:col-span-5 aspect-[16/10]" : "md:col-span-4 aspect-[4/5]"}`}
                 data-testid={`category-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <img src={c.image_url} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-br from-ink/50 to-ink/20 group-hover:from-magenta/40 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <h3 className="font-serif text-3xl md:text-5xl text-cream tracking-tight text-center px-6">{c.name}</h3>
                </div>
                <div className="absolute bottom-4 right-4 text-cream opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight className="w-6 h-6" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section id="products" className="py-24 md:py-32" data-testid="products-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-magenta mb-3">
                {activeCategory ? activeCategory : "The Edit"}
              </p>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1]">
                {activeCategory ? (
                  <>Showing <span className="italic">{activeCategory}</span></>
                ) : (
                  <>Currently <span className="italic">loved</span>.</>
                )}
              </h2>
            </div>
            {activeCategory ? (
              <button type="button" onClick={() => setActiveCategory(null)}
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-ink text-xs font-bold uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors"
                data-testid="clear-category-filter">
                Show all featured ×
              </button>
            ) : (
              <p className="text-sm text-ink/60 max-w-sm">Each piece is personally vetted. Affiliate links support the collective at no extra cost to you.</p>
            )}
          </div>
          {shownProducts.length === 0 ? (
            <div className="border border-ink/10 bg-cream-2 p-16 text-center">
              <p className="font-serif text-2xl mb-2">Nothing here yet.</p>
              <p className="text-sm text-ink/60">
                {activeCategory
                  ? `No products in ${activeCategory} yet. Add one from the admin dashboard.`
                  : "No featured products yet. Toggle 'Featured on landing' when adding products in admin."}
              </p>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {shownProducts.map((p, i) => (
              <motion.a key={p.id} href={p.affiliate_url} target="_blank" rel="noreferrer"
                 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                 className="group block bg-cream-2 border border-ink/10 hover:border-magenta transition-colors"
                 data-testid={`product-card-${i}`}>
                <div className="aspect-[4/5] overflow-hidden bg-ink">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-magenta">{p.category}</p>
                  <h3 className="font-serif text-xl mt-2 leading-tight">{p.name}</h3>
                  <p className="text-sm text-ink/60 mt-1">{p.brand}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm font-bold">{p.price_tier}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest group-hover:text-magenta transition-colors">
                      Shop <ArrowUpRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
          )}
        </div>
      </section>
      <section id="partner" className="relative py-24 md:py-32 bg-ink text-cream overflow-hidden" data-testid="partner-section">
        <img src={BG_SILK} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 to-ink/95" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-magenta mb-6">For Brands</p>
          <h2 className="font-serif text-5xl md:text-7xl italic tracking-tight leading-[1] max-w-4xl mx-auto">
            &ldquo;{s.partner_headline || "The right audience, approached with taste."}&rdquo;
          </h2>
          <p className="mt-10 max-w-2xl mx-auto text-cream/70 text-base md:text-lg leading-relaxed">
            {s.partner_body || "Charlie's Digital Collective works with premium brands."}
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <a href={`mailto:${s.contact_email || "partnerships@charliescollective.com"}`} className="inline-flex items-center gap-2 px-8 py-4 bg-magenta text-cream text-xs font-bold uppercase tracking-widest hover:bg-cream hover:text-ink transition-colors" data-testid="partner-email-cta">
              {s.contact_email || "partnerships@charliescollective.com"} <ArrowUpRight className="w-4 h-4" />
            </a>
            {s.paypal_url && (
              <a href={s.paypal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-cream text-cream text-xs font-bold uppercase tracking-widest hover:bg-cream hover:text-ink transition-colors" data-testid="partner-paypal-cta">
                Pay via PayPal <ArrowUpRight className="w-4 h-4" />
              </a>
            )}
          </div>
          <div className="mt-16 pt-12 border-t border-cream/10 grid grid-cols-3 max-w-2xl mx-auto text-left">
            <div><p className="font-serif text-4xl">6</p><p className="text-xs uppercase tracking-widest text-cream/60 mt-1">Categories</p></div>
            <div><p className="font-serif text-4xl">40+</p><p className="text-xs uppercase tracking-widest text-cream/60 mt-1">Brand partners</p></div>
            <div><p className="font-serif text-4xl">100%</p><p className="text-xs uppercase tracking-widest text-cream/60 mt-1">Personally vetted</p></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-ink/10" data-testid="footer">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-wrap justify-between gap-6 items-center">
          <p className="font-serif text-lg">Charlie&rsquo;s Digital Collective</p>
          <div className="flex items-center gap-5 text-ink/60">
            {socials.map(({ href, Icon, testid, label }) => (
              <a key={`f-${testid}`} href={href} target="_blank" rel="noreferrer" aria-label={label}><Icon className="w-5 h-5 hover:text-magenta transition-colors" /></a>
            ))}
          </div>
          <div className="flex gap-6 text-xs font-bold uppercase tracking-widest text-ink/60">
            <span>&copy; {new Date().getFullYear()} Charlie Smith</span>
            <Link to="/admin/login" data-testid="admin-link" className="hover:text-magenta transition-colors">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
